import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import Button from 'components/Button/Button';
import { Dropdown } from 'components/Forms/Dropdown/Dropdown';
import { InfoButtonOld } from 'components/InfoButton/InfoButtonOld';
import PhoneNumberInput from 'components/PhoneNumberInput/PhoneNumberInput';
import { TextInput } from 'components/TextInput/TextInput';
import months from 'data/months';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useAppLanguage from 'hooks/useAppLanguage';
import useCountry from 'hooks/useCountry';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { initiateSmsOtp } from 'store/otp/otp-actions';
import {
  useGetProfileQuery,
  useUpdateDetailsMutation,
} from 'store/slices/profile-api';
import { otpProcesses } from 'utils/constants';
import trimZero from 'utils/mobile-number';
import { isValidPhoneNumber } from 'utils/validation';
import { z } from 'zod';
import messages from '../../messages';

export const Route = createFileRoute('/auth/signup/_layout/account-details')({
  component: UpdateDetails,
});

const schema = z
  .object({
    firstName: z.string().min(1).max(255),
    lastName: z.string().min(1).max(255),
    countryCode: z.string().min(2).max(2),
    phoneNumber: z.string(),
    phoneNumberCountryCode: z.string(),
    dayOfBirth: z.number().min(1).max(31).optional(),
    monthOfBirth: z.string().optional(),
    yearOfBirth: z.string().optional(),
    dateOfBirth: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    // If day/month/year are provided, parse the date
    if (data.dayOfBirth && data.monthOfBirth && data.yearOfBirth) {
      const dateOfBirth = parseDate(
        data.monthOfBirth,
        data.yearOfBirth,
        data.dayOfBirth,
      );
      const now = moment();
      const differenceInYears = now.diff(dateOfBirth, 'year');
      if (differenceInYears < 18 || differenceInYears > 120) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please enter an age between 18 and 120 years old',
          path: ['dateOfBirth'],
        });
      }
      // You can mutate data to set the final dateOfBirth if it’s valid
      data.dateOfBirth = dateOfBirth;
    } else {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'You must specify day, month, and year of birth',
        path: ['dateOfBirth'],
      });
    }

    // Validate phone number if needed
    if (
      !isValidPhoneNumber(
        trimZero(data.phoneNumber),
        data.phoneNumberCountryCode,
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Ensure you enter a valid mobile number',
        path: ['phoneNumber'],
      });
    }
  });

const parseDate = (
  monthOfBirth: string,
  yearOfBirth: string,
  dayOfBirth: number,
) => {
  const monthIndex = moment().month(monthOfBirth).month(); // or parse it differently
  const dateOfBirth = moment([
    Number(yearOfBirth),
    monthIndex,
    Number(dayOfBirth),
  ]).format('YYYY-MM-DD');
  return dateOfBirth;
};

type FormSchema = z.infer<typeof schema>;

function UpdateDetails() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const language = useAppLanguage();
  const countries = useCountry();
  const updateDetailsMessages = messages[language].updateDetails;

  const [updateDetails, updateDetailsResult] = useUpdateDetailsMutation();
  const { data: profile } = useGetProfileQuery();

  const countryOptions = (countries ?? []).map(c => ({
    value: c.code,
    name: c.name,
  }));
  const monthOptions = months.map(x => {
    return { value: x, name: x };
  });

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isValid, errors },
  } = useForm<FormSchema>({
    resolver: zodResolver(schema),
    values: {
      firstName: profile?.firstName ?? '',
      lastName: profile?.lastName ?? '',
      countryCode: profile?.countryCode ?? '',
      phoneNumber: '',
      phoneNumberCountryCode: '',
      dateOfBirth: '',
    },
  });

  const onSubmit = handleSubmit(async data => {
    const result = await updateDetails({
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: trimZero(data.phoneNumber),
      phoneNumberCountryCode: data.phoneNumberCountryCode,
      dateOfBirth: data.dateOfBirth,
      countryCode: data.countryCode,
      //TODO:make nullable
      nationalID: '',
      passport: '',
    });

    if (!result.error) {
      dispatch(
        initiateSmsOtp(
          data.phoneNumberCountryCode,
          trimZero(data.phoneNumber),
          otpProcesses.SIGNUP_ENABLE_SMS_OTP,
        ),
      );
      navigate({
        to: '/auth/signup/otp',
      });
    }
  });

  return (
    <form onSubmit={() => console.log('test')}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="font-semibold leading-9 mt-6 text-2.5x1 text-black text-center tracking-wide">
          {updateDetailsMessages.heading}
        </h2>
        <p className="font-normal mt-3 text-center text-gray-900 text-lg tracking-wide mx-6 sm:mx-0">
          {updateDetailsMessages.subHeading}
        </p>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="m-6">
          <div className="w-full">
            <TextInput
              darkIcon={false}
              name="firstName"
              type="firstName"
              label={updateDetailsMessages.firstNameLabel}
              onChange={value => setValue('firstName', value)}
              tooltip={updateDetailsMessages.firstNameTooltip}
              value={watch('firstName')}
              ignoreSpaces
              mb-2
            />
            <TextInput
              name="lastName"
              type="lastName"
              label={updateDetailsMessages.surnameLabel}
              onChange={value => setValue('lastName', value)}
              value={watch('lastName')}
              ignoreSpaces
            />
          </div>

          <div className="w-full">
            <div className="my-6 relative">
              <Dropdown
                options={countryOptions}
                label={
                  <label className="block font-normal leading-5 text-gray-900">
                    {updateDetailsMessages.countryLabel}&nbsp;
                    <InfoButtonOld
                      name="country"
                      darkIcon={false}
                      relative
                      className="top-1.5">
                      <div>{updateDetailsMessages.countryTooltip}</div>
                    </InfoButtonOld>
                  </label>
                }
                value={watch('countryCode')}
                autocomplete
                onChange={value => {
                  setValue('countryCode', value);
                  if (!watch('phoneNumberCountryCode')) {
                    setValue('phoneNumberCountryCode', value);
                  }
                }}
              />
            </div>
          </div>
          <div className="pt-3">
            <label className="block font-normal leading-5 text-gray-900">
              {updateDetailsMessages.dateOfBirthLabel}
            </label>
            <div className="mt-1 relative rounded-md">
              <div className="-mt-px flex border-0">
                <div className="flex-1 min-w-0 w-1/4 ml-1 mr-2">
                  <input
                    data-browsing-ignore
                    className="bg-transparent border-gray-200 border-0 border-b-2 form-input rounded-none sm:leading-5 sm:text-md w-full focus:border-gray-200 py-2 px-3 focus:outline-none"
                    type="number"
                    placeholder={updateDetailsMessages.dayOfBirthLabel}
                    style={{ paddingLeft: 0 }}
                    value={watch('dayOfBirth')}
                    onChange={event => {
                      setValue(
                        'dayOfBirth',
                        event.target.value
                          ? Number(event.target.value)
                          : undefined,
                        {
                          shouldValidate: true,
                        },
                      );
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <Dropdown
                    options={monthOptions}
                    value={watch('monthOfBirth')}
                    autocomplete
                    preventNumbers
                    name="month"
                    placeholder={updateDetailsMessages.monthOfBirthLabel}
                    onChange={value => setValue('monthOfBirth', value)}
                  />
                </div>
                <div className="md-input-box flex-1 min-w-0 ml-2">
                  <input
                    data-browsing-ignore
                    className="bg-transparent border-gray-200 border-0 border-b-2 form-input rounded-none sm:leading-5 sm:text-md w-full py-2 px-3 focus:outline-none"
                    type="number"
                    value={watch('yearOfBirth')}
                    placeholder={updateDetailsMessages.yearOfBirthLabel}
                    onChange={event => {
                      setValue('yearOfBirth', event.target.value, {
                        shouldValidate: true,
                      });
                    }}
                  />
                </div>
              </div>
              {errors && errors.dateOfBirth && (
                <div className="text-error text-sm">
                  {errors.dateOfBirth.message}
                </div>
              )}
            </div>
          </div>

          <div className="mt-2 w-full">
            <PhoneNumberInput
              noPaddingX
              lightMode
              phoneNumber={''}
              dialCode={''}
              countries={countries}
              country={watch('phoneNumberCountryCode')}
              countryPicture={watch(
                'phoneNumberCountryCode',
              )?.toLocaleLowerCase()}
              onChangeCountry={value =>
                setValue('phoneNumberCountryCode', value, {
                  shouldValidate: true,
                })
              }
              onChangeNumber={value =>
                setValue('phoneNumber', value, { shouldValidate: true })
              }
              countryLabel={updateDetailsMessages.dialCodeLabel}
            />

            {errors && errors.phoneNumber && (
              <div className="text-error text-sm">
                {errors.phoneNumber.message}
              </div>
            )}
          </div>

          <div className="mt-6">
            <span className="block w-full rounded-md">
              <Button
                loading={updateDetailsResult.isLoading}
                onClick={onSubmit}
                disabled={!isValid}
                w-full
                flex
                justify-center
                py-2
                px-4
                border
                border-transparent
                text-sm
                font-normal
                rounded-md
                text-white
                transition
                duration-150
                ease-in-out
                bg-vivid-blue
                type="submit">
                {updateDetailsMessages.continueButton}
              </Button>
            </span>
          </div>

          <div className="mt-6">
            <span className="block text-center">
              {updateDetailsMessages.alreadyHaveAnAccount}&nbsp;
              <Link
                to="/auth/login"
                className="font-semibold text-vivid-blue underline hover:no-underline">
                {updateDetailsMessages.logInLink}
              </Link>
            </span>
          </div>
        </div>
      </div>
    </form>
  );
}
